import axios from 'axios';
import { Lunar } from 'lunar-javascript';
import type { Moment } from 'moment';
import moment from 'moment';

import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';

const solarHolidays = ['01-01', '02-28', '04-04', '04-05', '05-01', '10-10'];
const lunarHoliday = ['1-1', '1-2', '1-3', '1-4', '1-5', '5-5', '8-15'];

export const disabledTimes = (allowDates: string[]) => {
  return (date: Moment, type: 'day' | 'month' | 'year'): boolean => {
    let formatter = 'YYYY-MM-DD';
    if (type === 'month') formatter = 'YYYY-MM';
    if (type === 'year') formatter = 'YYYY';
    const dateString = date.format(formatter);
    return (
      allowDates.find((allowDate) => allowDate.match(dateString)) === undefined
    );
  };
};

const checkIsWeekend = () => {
  return moment().get('day') === 0 || moment().get('day') === 6;
};

const checkIsHoliday = () => {
  const todayDate = moment().format('MM-DD');
  const lunarMonth: number = Lunar.fromDate(new Date()).getMonth();
  const lunarDate: number = Lunar.fromDate(new Date()).getDay();
  const lunarToday: string = lunarMonth + '-' + lunarDate;

  return solarHolidays.includes(todayDate) || lunarHoliday.includes(lunarToday);
};

export const getExpenseRate = (expenseSetting: ChargingPileElectricityRate) => {
  const rateNames = {
    summer: {
      peak: '夏季尖峰',
      offPeak: '夏季離峰',
    },
    nonSummer: {
      peak: '非夏季尖峰',
      offPeak: '非夏季離峰',
    },
  };
  const now = moment();
  const summerDates = expenseSetting.summer.dates[0];
  const season = now.isBetween(
    ...(summerDates.map((d) => moment(d, 'MM-DD')) as [
      moment.Moment,
      moment.Moment
    ]),
    'day',
    '[]'
  )
    ? 'summer'
    : 'nonSummer';
  let peakTimes: [moment.Moment, moment.Moment][];
  if (season === 'summer') {
    peakTimes = expenseSetting.summer.peak.hours.map(([start, end]) => [
      moment(start, 'HH:mm'),
      moment(end, 'HH:mm'),
    ]);
  } else {
    peakTimes = expenseSetting.nonSummer.peak.hours.map(([start, end]) => [
      moment(start, 'HH:mm'),
      moment(end, 'HH:mm'),
    ]);
  }

  const times = peakTimes.some(([start, end]) => {
    if (checkIsWeekend()) {
      return false;
    } else {
      if (checkIsHoliday() === false) {
        return now.isBetween(start, end, 'minute', '[]');
      } else {
        return false;
      }
    }
  })
    ? 'peak'
    : 'offPeak';
  return {
    rateName: rateNames[season][times],
    rate: expenseSetting[season][times].price.toString(),
    lastUpdateMinute: moment().endOf('minute'),
  };
};

export const getUsageData = async (pile: ChargingPile) => {
  try {
    const { data } = await axios.get(
      `/ezcon/api/returnMonthlyData?chargingPileID=${
        pile.chargingPileID
      }&timeStamp=${moment().valueOf()}`,
      {
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      }
    );
    const { totalEnergy, totalExpense } = data[0];
    return {
      usage: totalEnergy,
      cost: totalExpense,
      updateTime: moment().format('MM-DD HH:mm'),
    };
  } catch (err) {
    console.error(err);
    return null;
  }
};
